import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ConfigProvider, Menu } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
	TbChevronsLeft,
	TbChevronsRight,
	TbSearch,
	TbApps,
} from 'react-icons/tb';

import EdenContext from '../../context/edenContext';
import Select from '../select';
import ToolTip from '../tooltip';
import axiosInstance from '../../api/axiosInstance';
import { journeyStages, FEATURED_PATHS } from '../../constant/journeyData';
import { stageIcons } from '../../constant/stageIcons';

import styles from './sidebar.module.scss';

const PINNED_KEYS = new Set(['/', '/help-desk']);

const MENU_THEME = {
	itemColor: '#404040',
	itemHoverColor: '#111827',
	itemHoverBg: '#ffffff',
	itemSelectedColor: '#111827',
	itemSelectedBg: '#ffffff',
	subMenuItemSelectedColor: '#111827',
	itemBorderRadius: 6,
	itemMarginInline: 6,
	itemPaddingInline: 12,
	subMenuItemBg: 'transparent',
	iconSize: 18,
	collapsedWidth: 56,
};

const flattenMenuItems = (items) =>
	(items || []).flatMap((item) =>
		item.children ? flattenMenuItems(item.children) : [item],
	);

const Sidebar = ({ collapsed, toggleCollapsed, setCollapsed }) => {
	const { userType, userMail, menuItems } = useContext(EdenContext);

	const location = useLocation();
	const navigate = useNavigate();
	const [leaveTime, setLeaveTime] = useState(null);
	const searchRef = useRef(null);

	useEffect(() => {
		setLeaveTime(new Date());
	}, [location.pathname]);

	useEffect(() => {
		if (location.pathname === '/raven') {
			setCollapsed(true);
		}
	}, [location.pathname, setCollapsed]);

	useEffect(() => {
		const handleKeyDown = (event) => {
			const isSearchShortcut =
				(event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k';
			if (!isSearchShortcut) return;
			event.preventDefault();
			if (collapsed) {
				setCollapsed(false);
			} else {
				searchRef.current?.focus();
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [collapsed, setCollapsed]);

	const flatItems = useMemo(() => flattenMenuItems(menuItems), [menuItems]);

	const accessiblePaths = useMemo(
		() => new Set(flatItems.map((item) => item.key.split('?')[0])),
		[flatItems],
	);

	const pinnedItems = useMemo(
		() => flatItems.filter((item) => PINNED_KEYS.has(item.key)),
		[flatItems],
	);

	const stageGroups = useMemo(
		() =>
			journeyStages
				.map((stage) => {
					const StageIcon = stageIcons[stage.icon];
					return {
						key: `group-${stage.id}`,
						label: stage.title,
						icon: StageIcon ? (
							<span
								className={styles['stage-icon']}
								style={{ '--stage-color': stage.color }}
							>
								<StageIcon />
								<span className={styles['stage-badge']}>{stage.number}</span>
							</span>
						) : null,
						children: stage.products
							.filter(
								(product) =>
									product.route &&
									accessiblePaths.has(product.route.split('?')[0]),
							)
							.map((product) => ({ key: product.route, label: product.name })),
					};
				})
				.filter((group) => group.children.length > 0),
		[accessiblePaths],
	);

	const otherToolsGroup = useMemo(() => {
		const children = flatItems
			.filter(
				(item) =>
					!PINNED_KEYS.has(item.key) &&
					!item.key.startsWith('/admin') &&
					!FEATURED_PATHS.has(item.key.split('?')[0]),
			)
			.map((item) => ({ key: item.key, label: item.label }));

		return children.length > 0
			? [
					{
						key: 'group-more-in-eden',
						label: 'More in EDEN',
						icon: <TbApps />,
						children,
					},
				]
			: [];
	}, [flatItems]);

	const adminGroup = useMemo(() => {
		const adminItem = (menuItems || []).find((item) => item.key === '/admin');
		if (!adminItem?.children?.length) return [];
		return [
			{
				key: '/admin',
				label: 'Admin',
				icon: adminItem.icon,
				children: adminItem.children,
			},
		];
	}, [menuItems]);

	const items = useMemo(
		() => [
			...pinnedItems.map((item) => ({
				key: item.key,
				label: item.label,
				icon: item.icon,
			})),
			...stageGroups,
			...otherToolsGroup,
			...adminGroup,
		],
		[pinnedItems, stageGroups, otherToolsGroup, adminGroup],
	);

	const allLeafKeys = useMemo(() => {
		const keys = [];
		const walk = (list) =>
			list.forEach((item) => {
				if (item.children) walk(item.children);
				else keys.push(item.key);
			});
		walk(items);
		return keys;
	}, [items]);

	const activeKey = useMemo(() => {
		let bestKey = null;
		let bestScore = -1;
		allLeafKeys.forEach((key) => {
			const [itemPath, itemQuery] = key.split('?');
			if (location.pathname !== itemPath) return;
			const score = itemQuery
				? location.search.slice(1) === itemQuery
					? 2
					: -1
				: 1;
			if (score > bestScore) {
				bestScore = score;
				bestKey = key;
			}
		});
		return bestKey;
	}, [allLeafKeys, location.pathname, location.search]);

	const activeGroupKey = useMemo(() => {
		const found = items.find((item) =>
			item.children?.some((child) => child.key === activeKey),
		);
		return found?.key;
	}, [items, activeKey]);

	const onClick = ({ key }) => {
		navigate(key);
		setCollapsed(true);

		const leDate = new Date(leaveTime?.toISOString());
		const entDate = new Date(new Date().toISOString());
		const diffInMilliseconds = Math.abs(leDate - entDate);
		const url = window.location.pathname;
		const sectionName = url
			?.substring(url?.lastIndexOf('/'))
			.replace(/[/-]/g, ' ')
			.toUpperCase()
			.trim();

		const data = {
			user_email: userMail,
			section_name: sectionName,
			section_enter_time: leaveTime?.toISOString(),
			section_leave_time: new Date().toISOString(),
			total_duration_seconds: diffInMilliseconds / 1000,
			event_datetime: '',
			comment: '',
		};

		axiosInstance
			.post(
				`https://internal.supremecomponents.com/api/track.php`,
				JSON.stringify(data),
			)
			.then((response) => {});
	};

	const searchOptions = flatItems.map((item) => ({
		value: item.key,
		label: item.key.startsWith('/admin/')
			? `Admin - ${item.label}`
			: item.label,
	}));

	return (
		<div className={styles['sidebar-wrapper']}>
			<div
				onClick={toggleCollapsed}
				className={`${styles['collapse-btn']} ${collapsed ? styles['collapse-btn-collapsed'] : ''}`}
			>
				{collapsed ? <TbChevronsRight /> : <TbChevronsLeft />}
			</div>
			{collapsed === true ? (
				<div className={styles['search-icon']}>
					<ToolTip title="Search Section" placement="right">
						<TbSearch
							onClick={() => {
								setCollapsed(false);
							}}
						/>
					</ToolTip>
				</div>
			) : (
				<div className={styles['search-menu']}>
					<Select
						ref={searchRef}
						autoFocus={true}
						type="bordered"
						placeholder="Search (⌘K)"
						optionFilterProp={'label'}
						labelInValue={true}
						style={{ width: '260px', height: 40 }}
						options={searchOptions}
						showSearch
						onChange={(value) => {
							navigate(value?.value);
							setCollapsed(true);
						}}
					/>
				</div>
			)}
			<ConfigProvider theme={{ components: { Menu: MENU_THEME } }}>
				<Menu
					key={collapsed ? 'collapsed' : activeGroupKey}
					className={` ${styles['side']} ${
						collapsed === true
							? ''
							: userType === 'sm'
								? styles['sidebar-sm']
								: styles['sidebar']
					}`}
					mode="inline"
					inlineIndent={12}
					defaultOpenKeys={collapsed ? [] : [activeGroupKey].filter(Boolean)}
					inlineCollapsed={collapsed}
					items={items}
					triggerSubMenuAction={'click'}
					onClick={onClick}
					selectedKeys={activeKey ? [activeKey] : []}
				/>
			</ConfigProvider>
		</div>
	);
};

export default Sidebar;
